import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, updateDoc, query, where, addDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './AdminCalendario.css';
import FloatingMenu from './FloatingMenu';
// Importar jsPDF correctamente
import { jsPDF } from 'jspdf';
// Importar autoTable como un plugin que extiende jsPDF
import autoTable from 'jspdf-autotable';
// Importar el servicio de email
import { enviarCorreoConfirmacion } from '../services/emailService';
import { obtenerPrecioPorOcupacionYFecha } from '../services/preciosService';

const AdminCalendario = () => {
  // Estados para el calendario
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  // Initialize rooms as empty array instead of using habitaciones.js data
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados para el modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);
  const [newReservation, setNewReservation] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    rooms: [], // Array para almacenar múltiples habitaciones
    reservationCode: '',
    duration: 1,
    totalPrice: 0,
    calendarEventId: '',
    syncDate: '',
    syncWithCalendar: false,
    tipoPago: 'Efectivo',
    sendEmail: true
  });

  // Nuevo estado para controlar el menú de herramientas avanzadas
  const [showAdvancedTools, setShowAdvancedTools] = useState(false);

  // Nuevo estado para controlar el envío del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para el bloqueo múltiple
  const [multiBlockMode, setMultiBlockMode] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [blockStartDate, setBlockStartDate] = useState('');
  const [blockEndDate, setBlockEndDate] = useState('');

  // Nuevo estado para manejar la ocupación por habitación
  const [roomOccupancy, setRoomOccupancy] = useState({});

  // Añadir un nuevo estado para almacenar los precios calculados por habitación
  const [roomPrices, setRoomPrices] = useState({});

  const location = useLocation();

  // Función para manejar el inicio del modo de bloqueo múltiple
  const startMultiBlockMode = () => {
    setMultiBlockMode(true);
    setSelectedRooms([]);
    setBlockStartDate('');
    setBlockEndDate('');
  };

  // Función para manejar la selección de habitaciones
  const handleRoomSelection = (roomId, occupancy) => {
    setRoomOccupancy(prev => ({
      ...prev,
      [roomId]: occupancy
    }));
    
    if (!selectedRooms.includes(roomId)) {
      const updatedRooms = [...selectedRooms, roomId];
      setSelectedRooms(updatedRooms);
      
      // Actualizar precio total con la nueva habitación
      const updatedOccupancy = {
        ...roomOccupancy,
        [roomId]: occupancy
      };
      calculateTotalPrice(updatedRooms, updatedOccupancy, newReservation.duration);
      } else {
      // Solo actualizar ocupación
      const updatedOccupancy = {
        ...roomOccupancy,
        [roomId]: occupancy
      };
      calculateTotalPrice(selectedRooms, updatedOccupancy, newReservation.duration);
    }
  };

  // Función para remover una habitación
  const handleRemoveRoom = (roomId) => {
    const updatedRooms = selectedRooms.filter(id => id !== roomId);
    setSelectedRooms(updatedRooms);
    
    setRoomOccupancy(prev => {
      const newOccupancy = { ...prev };
      delete newOccupancy[roomId];
      
      // Actualizar precio total sin esta habitación
      calculateTotalPrice(updatedRooms, newOccupancy, newReservation.duration);
      
      return newOccupancy;
    });
  };

  // Función para confirmar el bloqueo múltiple
  const confirmMultiBlock = async () => {
    if (!blockStartDate || !blockEndDate || selectedRooms.length === 0) {
      setError('Por favor, seleccione fechas y al menos una habitación');
      return;
    }

    try {
      setIsSubmitting(true);
      // Asegurarnos de que las fechas se creen correctamente
      const startDate = new Date(blockStartDate + 'T12:00:00');
      const endDate = new Date(blockEndDate + 'T12:00:00');

      // Calcular la duración correctamente
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Crear bloqueos para cada habitación seleccionada
      for (const roomId of selectedRooms) {
        const reservationData = {
          nombre: "Bloqueado",
          name: "Bloqueado",
          email: "bloqueado@sistema.interno",
          telefono: "-",
          phone: "-",
          fechaEntrada: startDate,
          checkIn: startDate,
          fechaSalida: endDate,
          checkOut: endDate,
          fechaCreacion: new Date(),
          habitacionId: parseInt(roomId),
          roomId: parseInt(roomId),
          personasEnHabitacion: 0,
          guests: 0,
          duracionEstancia: diffDays,
          duration: diffDays,
          precioHabitacion: 0,
          roomPrice: 0,
          precioTotal: 0,
          totalPrice: 0,
          codigoReserva: Math.floor(100000 + Math.random() * 900000).toString(),
          reservationCode: Math.floor(100000 + Math.random() * 900000).toString(),
          status: 'confirmed'
        };

        await addDoc(collection(db, 'reservas'), reservationData);
      }

      setSuccess(`Se han bloqueado ${selectedRooms.length} habitaciones del ${blockStartDate} al ${blockEndDate}`);
      setMultiBlockMode(false);
      await fetchReservationsForMonth(currentMonth, currentYear);
    } catch (err) {
      console.error("Error al crear bloqueos múltiples:", err);
      setError(`Error al crear bloqueos: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para desplazar al día actual
  const scrollToCurrentDay = () => {
    const today = new Date();
    const isCurrentMonth = today.getMonth() === currentMonth && today.getFullYear() === currentYear;
    
    if (!isCurrentMonth) {
      // Si no estamos en el mes actual, cambiar al mes actual
      setCurrentMonth(today.getMonth());
      setCurrentYear(today.getFullYear());
      return;
    }
    
    const currentDayElement = document.getElementById('current-day-column');
    if (currentDayElement) {
      // Calcular la posición para desplazarse
      const container = document.querySelector('.calendar-container');
      const cornerCell = document.querySelector('.corner-cell');
      
      if (container && cornerCell) {
        const cornerWidth = cornerCell.offsetWidth;
        const leftPosition = currentDayElement.offsetLeft - cornerWidth;
        
        // Desplazar el contenedor para que el día actual sea la primera columna visible
        container.scrollLeft = leftPosition;
      }
    }
  };

  // Array de meses para mostrar en el encabezado
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Obtener días en el mes actual
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Cargar habitaciones y reservaciones al inicio
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch rooms from Firestore habitaciones collection instead of using local data
        const habitacionesCollection = collection(db, 'habitaciones');
        const habitacionesSnapshot = await getDocs(habitacionesCollection);
        
        if (habitacionesSnapshot.docs.length > 0) {
          const roomsList = habitacionesSnapshot.docs.map(doc => {
            // Obtener nombre directamente de los datos
            const nombreHabitacion = doc.data().nombre || 'Sin nombre';
            
            return {
              id: doc.id.toString(),
              name: nombreHabitacion, // Mantener por compatibilidad
              nombre: nombreHabitacion, // Añadir también como 'nombre'
              tipo: doc.data().tipo || 'Estándar',
              capacidad: doc.data().capacity || 1,
              precio: doc.data().precio || 0,
              preciosPorOcupacion: doc.data().preciosPorOcupacion || {} // Incluir precios por ocupación
            };
          });
          
          // Sort rooms by ID
          roomsList.sort((a, b) => {
            const idA = parseInt(a.id);
            const idB = parseInt(b.id);
            return idA - idB;
          });
          
          setRooms(roomsList);
        } else {
          console.warn('No se encontraron habitaciones en la base de datos.');
          setRooms([]);
        }
        
        // Fetch reservations for current month
        await fetchReservationsForMonth(currentMonth, currentYear);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error al cargar datos: ' + err.message);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentMonth, currentYear]);

  // Efecto para desplazar al día actual cuando el calendario esté cargado
  useEffect(() => {
    if (!loading) {
      // Solo desplazar al día actual si estamos en el mes actual
      const today = new Date();
      if (today.getMonth() === currentMonth && today.getFullYear() === currentYear) {
        setTimeout(scrollToCurrentDay, 100);
      }
    }
  }, [loading, currentMonth, currentYear]);

  // Efecto para cargar reserva para editar desde URL params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const reservationIdToEdit = queryParams.get('editReservation');
    
    const editReservationFromParams = async () => {
      if (reservationIdToEdit && !loading && rooms.length > 0) {
        try {
          console.log("Cargando reserva para editar desde URL:", reservationIdToEdit);
          // Obtener la reserva específica
          const reservaDoc = doc(db, 'reservas', reservationIdToEdit);
          const reservaSnapshot = await getDoc(reservaDoc);
          
          if (reservaSnapshot.exists()) {
            const reservation = {
              id: reservaSnapshot.id,
              ...reservaSnapshot.data()
            };
            
            console.log("Reserva encontrada:", reservation);
            
            // Formatear fechas para el formulario
            let checkInDate, checkOutDate;
            
            // Procesar fechaEntrada
            if (reservation.fechaEntrada instanceof Timestamp) {
              checkInDate = reservation.fechaEntrada.toDate();
            } else if (typeof reservation.fechaEntrada === 'object' && reservation.fechaEntrada.seconds) {
              checkInDate = new Date(reservation.fechaEntrada.seconds * 1000);
            } else if (reservation.fechaEntrada instanceof Date) {
              checkInDate = reservation.fechaEntrada;
            } else {
              checkInDate = new Date(reservation.fechaEntrada || reservation.checkIn);
            }
            
            // Procesar fechaSalida
            if (reservation.fechaSalida instanceof Timestamp) {
              checkOutDate = reservation.fechaSalida.toDate();
            } else if (typeof reservation.fechaSalida === 'object' && reservation.fechaSalida.seconds) {
              checkOutDate = new Date(reservation.fechaSalida.seconds * 1000);
            } else if (reservation.fechaSalida instanceof Date) {
              checkOutDate = reservation.fechaSalida;
            } else {
              checkOutDate = new Date(reservation.fechaSalida || reservation.checkOut);
            }
            
            // Formatear fechas a YYYY-MM-DD para los inputs de fecha
            const checkInStr = `${checkInDate.getFullYear()}-${(checkInDate.getMonth() + 1).toString().padStart(2, '0')}-${checkInDate.getDate().toString().padStart(2, '0')}`;
            const checkOutStr = `${checkOutDate.getFullYear()}-${(checkOutDate.getMonth() + 1).toString().padStart(2, '0')}-${checkOutDate.getDate().toString().padStart(2, '0')}`;
            
            // Calcular duración en días
            const diffTime = Math.abs(checkOutDate - checkInDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            const roomId = (reservation.habitacionId || reservation.roomId || "1").toString();
            
            // IMPORTANTE: Obtener el precio por noche de forma correcta
            // Intentar obtener el precio por noche en el siguiente orden de preferencia:
            // 1. Campo específico precioPorNoche o pricePerNight
            // 2. Campo precioHabitacion o roomPrice
            // 3. Calcular a partir del precio total
            
            let pricePerNight = null;
            
            if (reservation.precioPorNoche) {
              pricePerNight = reservation.precioPorNoche;
              console.log("URL: Cargando precio por noche desde campo 'precioPorNoche':", pricePerNight);
            } else if (reservation.pricePerNight) {
              pricePerNight = reservation.pricePerNight;
              console.log("URL: Cargando precio por noche desde campo 'pricePerNight':", pricePerNight);
            } else if (reservation.precioHabitacion) {
              pricePerNight = reservation.precioHabitacion;
              console.log("URL: Cargando precio por noche desde campo 'precioHabitacion':", pricePerNight);
            } else if (reservation.roomPrice) {
              pricePerNight = reservation.roomPrice;
              console.log("URL: Cargando precio por noche desde campo 'roomPrice':", pricePerNight);
            } else {
              // Si no podemos encontrar ningún precio específico, calcular desde el precio total
              const totalPrice = reservation.precioTotal || reservation.totalPrice || 0;
              pricePerNight = diffDays > 0 ? Math.round(totalPrice / diffDays) : 0;
              console.log("URL: Precio por noche calculado desde precioTotal:", pricePerNight);
            }
            
            console.log("URL: Precio por noche final para formulario:", pricePerNight);
            
            // Objeto completo para depuración
            console.log("URL: Valores importantes:", {
              precioPorNoche: reservation.precioPorNoche,
              pricePerNight: reservation.pricePerNight,
              precioHabitacion: reservation.precioHabitacion,
              roomPrice: reservation.roomPrice,
              precioTotal: reservation.precioTotal,
              totalPrice: reservation.totalPrice,
              diffDays: diffDays,
              pricePerNightFinal: pricePerNight
            });
            
            // Obtener datos de la reserva
            setNewReservation({
              name: reservation.nombre || reservation.name || '',
              email: reservation.email || '',
              phone: reservation.telefono || reservation.phone || '',
              checkIn: checkInStr,
              checkOut: checkOutStr,
              rooms: [roomId], // Convertir a array
              reservationCode: reservation.codigoReserva || reservation.reservationCode || '',
              duration: diffDays,
              totalPrice: reservation.precioTotal || reservation.totalPrice || 0,
              pricePerNight: pricePerNight, // Usar el precio obtenido correctamente
              calendarEventId: reservation.calendarEventId || '',
              syncDate: reservation.fechaSincronizacion || reservation.syncDate || '',
              syncWithCalendar: reservation.sincronizadoCalendario || reservation.syncWithCalendar || false,
              tipoPago: reservation.tipoPago || reservation.metodoPago || 'Efectivo',
              sendEmail: false // No enviar correo al editar
            });
            
            // Establecer modo edición y abrir el modal
            setModalMode('edit');
            setEditingReservation({
              ...reservation,
              id: reservation.id || ''
            });
            setIsModalOpen(true);
            
            // Ir al mes de la reserva
            setCurrentMonth(checkInDate.getMonth());
            setCurrentYear(checkInDate.getFullYear());
          } else {
            console.log("No se encontró la reserva con ID:", reservationIdToEdit);
          }
        } catch (error) {
          console.error("Error al cargar la reserva desde URL:", error);
          setError(`Error al cargar la reserva: ${error.message}`);
        }
      }
    };
    
    if (rooms.length > 0 && !loading) {
      editReservationFromParams();
    }
  }, [location.search, rooms, loading]);

  // Obtener reservaciones para el mes actual
  const fetchReservationsForMonth = async (month, year) => {
    try {
      const startOfMonth = new Date(year, month, 1);
      startOfMonth.setHours(0, 0, 0, 0);
      const endOfMonth = new Date(year, month + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      
      // Calcular el primer día del mes siguiente para detectar reservas en límite
      const firstDayNextMonth = new Date(year, month + 1, 1);
      firstDayNextMonth.setHours(0, 0, 0, 0);
      
      const startDateString = startOfMonth.toISOString().split('T')[0];
      const endDateString = endOfMonth.toISOString().split('T')[0];
      
      console.log(`Buscando reservas entre ${startDateString} y ${endDateString}`);
      
      // Obtenemos todas las reservas para depuración
      const reservasCollection = collection(db, 'reservas');
      const allReservationsSnapshot = await getDocs(reservasCollection);
      const allReservationsList = allReservationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`Se encontraron ${allReservationsList.length} reservas en total`);
      
      // Filtramos manualmente por fecha
      const filteredReservations = allReservationsList.filter(res => {
        // Obtenemos la fecha de entrada y salida independientemente del nombre del campo
        const checkInField = res.fechaEntrada || res.checkIn;
        const checkOutField = res.fechaSalida || res.checkOut;
        
        if (!checkInField || !checkOutField) {
          console.log("Reserva sin fechas:", res);
          return false;
        }
        
        let checkIn, checkOut;
        
        // Manejar diferentes formatos de fecha para checkIn
        if (typeof checkInField === 'object' && checkInField.seconds) {
          // Timestamp de Firestore
          checkIn = new Date(checkInField.seconds * 1000);
        } else if (checkInField instanceof Date) {
          // Objeto Date
          checkIn = checkInField;
        } else if (typeof checkInField === 'string') {
          // Cadena ISO
          checkIn = new Date(checkInField);
        } else {
          console.log("Formato de fecha de entrada no reconocido:", checkInField);
          return false;
        }
        
        // Manejar diferentes formatos de fecha para checkOut
        if (typeof checkOutField === 'object' && checkOutField.seconds) {
          checkOut = new Date(checkOutField.seconds * 1000);
        } else if (checkOutField instanceof Date) {
          checkOut = checkOutField;
        } else if (typeof checkOutField === 'string') {
          checkOut = new Date(checkOutField);
        } else {
          console.log("Formato de fecha de salida no reconocido:", checkOutField);
          return false;
        }
        
        // Normalizar horas para comparación de fechas
        checkIn.setHours(0, 0, 0, 0);
        checkOut.setHours(0, 0, 0, 0);
        
        // CASO ESPECIAL: Detectar reservas que comienzan el último día del mes
        // y terminan el primer día del mes siguiente
        const isLastDayToFirstDay = 
          checkIn.getTime() === endOfMonth.setHours(0, 0, 0, 0) && 
          checkOut.getTime() === firstDayNextMonth.getTime();
        
        // Verificar si hay solapamiento de fechas (la condición normal)
        const overlap = checkOut > startOfMonth && checkIn < new Date(year, month + 1, 0, 23, 59, 59, 999);
        
        // Incluir reservas del caso especial explícitamente
        const shouldInclude = overlap || isLastDayToFirstDay;
        
        if (shouldInclude) {
          console.log(`Reserva incluida: ${res.id}, Check-in: ${checkIn.toISOString().split('T')[0]}, Check-out: ${checkOut.toISOString().split('T')[0]}`);
          console.log("Detalles adicionales:", {
            overlap: overlap,
            isLastDayToFirstDay: isLastDayToFirstDay,
            checkIn: checkIn.toISOString(),
            checkOut: checkOut.toISOString(),
            startOfMonth: startOfMonth.toISOString(),
            endOfMonth: new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString(),
            firstDayNextMonth: firstDayNextMonth.toISOString()
          });
        }
        
        return shouldInclude;
      });
      
      console.log(`Se encontraron ${filteredReservations.length} reservas para este mes`);
      
      // Mostrar detalles de las reservas encontradas
      filteredReservations.forEach((res, index) => {
        console.log(`Reserva ${index + 1}:`, {
          id: res.id,
          habitación: res.habitacionId || res.roomId,
          nombre: res.nombre || res.name || 'Sin nombre',
          checkIn: res.fechaEntrada || res.checkIn,
          checkOut: res.fechaSalida || res.checkOut
        });
      });
      
      setReservations(filteredReservations);
      
      if (filteredReservations.length === 0 && allReservationsList.length > 0) {
        setError(`No hay reservas para ${months[month]} ${year}. Hay ${allReservationsList.length} reservas en total en la base de datos.`);
      }
    } catch (err) {
      console.error("Error al obtener reservaciones:", err);
      setError(`Error al cargar las reservaciones: ${err.message}`);
    }
  };

  // Manejar cambio a mes anterior
  const handlePrevMonth = async () => {
    const newMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const newYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    // Primero cargar las reservas para el nuevo mes
    await fetchReservationsForMonth(newMonth, newYear);
    
    // Luego actualizar el estado
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  // Manejar cambio a mes siguiente
  const handleNextMonth = async () => {
    const newMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const newYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    
    // Primero cargar las reservas para el nuevo mes
    await fetchReservationsForMonth(newMonth, newYear);
    
    // Luego actualizar el estado
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  // Comprobar si un día está reservado para una habitación específica
  const isDayReserved = (day, roomId) => {
    // Crear la fecha del día actual a las 12:00 para evitar problemas de zona horaria
    const dayDate = new Date(currentYear, currentMonth, day, 12, 0, 0);
    
    // Para depuración
    console.log(`Comprobando reservas para día ${dayDate.toISOString().split('T')[0]} y habitación ${roomId}`);
    
    // Convertir roomId a string para comparación consistente
    const roomIdStr = roomId.toString();
    
    // Buscar una reserva que coincida con este día y esta habitación
    const reservation = reservations.find(reservation => {
      // Comprobar los campos de fecha - pueden usar fechaEntrada/fechaSalida o checkIn/checkOut
      let checkInField = reservation.fechaEntrada || reservation.checkIn;
      let checkOutField = reservation.fechaSalida || reservation.checkOut;
      const roomIdField = reservation.habitacionId || reservation.roomId;
      
      // Validar que la reserva tenga los campos necesarios
      if (!checkInField || !checkOutField || !roomIdField) {
        console.log("Reserva sin campos requeridos:", reservation);
        return false;
      }
      
      try {
        // Crear fechas de entrada y salida a las 12:00 para evitar problemas de zona horaria
        let checkIn, checkOut;
        
        // Manejar diferentes formatos de fecha
        if (typeof checkInField === 'object' && checkInField.seconds) {
          // Timestamp de Firestore
          checkIn = new Date(checkInField.seconds * 1000);
        } else if (checkInField instanceof Date) {
          // Objeto Date
          checkIn = checkInField;
        } else if (typeof checkInField === 'string') {
          // Cadena ISO o formato de fecha
          checkIn = new Date(checkInField);
        } else {
          console.log("Formato de fecha de entrada no reconocido:", checkInField);
          return false;
        }
        
        if (typeof checkOutField === 'object' && checkOutField.seconds) {
          checkOut = new Date(checkOutField.seconds * 1000);
        } else if (checkOutField instanceof Date) {
          checkOut = checkOutField;
        } else if (typeof checkOutField === 'string') {
          checkOut = new Date(checkOutField);
        } else {
          console.log("Formato de fecha de salida no reconocido:", checkOutField);
          return false;
        }
        
        // Establecer la hora a las 12 del mediodía para evitar problemas de zona horaria
        checkIn.setHours(12, 0, 0, 0);
        checkOut.setHours(12, 0, 0, 0);
        dayDate.setHours(12, 0, 0, 0);
        
        // Convertir el habitacionId de la reserva a string
        const reservationRoomId = roomIdField.toString();
        
        // IMPORTANTE: Para reservas que terminan el primer día del siguiente mes, 
        // la comparación debe ser: dayDate >= checkIn && dayDate < checkOut
        // Incluye el día de entrada (>=) pero no el día de salida (<)
        const isDayInRange = dayDate >= checkIn && dayDate < checkOut;
        const isCorrectRoom = reservationRoomId === roomIdStr;
        
        // Verificar específicamente si es el último día del mes y la reserva continúa
        const isLastDayOfMonth = day === getDaysInMonth(currentMonth, currentYear);
        const crossesMonthBoundary = isLastDayOfMonth && checkOut > new Date(currentYear, currentMonth + 1, 1, 12, 0, 0);
        
        // Log para depuración
        if ((isDayInRange || crossesMonthBoundary) && isCorrectRoom) {
          console.log(`✅ Reserva encontrada para habitación ${roomIdStr} en fecha ${dayDate.toISOString().split('T')[0]}`);
          console.log('Detalles:', {
            reservationId: reservation.id,
            name: reservation.nombre || reservation.name,
            checkIn: checkIn.toISOString().split('T')[0],
            checkOut: checkOut.toISOString().split('T')[0],
            roomId: roomIdField,
            isLastDayOfMonth: isLastDayOfMonth,
            crossesMonthBoundary: crossesMonthBoundary
          });
        }
        
        return (isDayInRange && isCorrectRoom);
      } catch (err) {
        console.error('Error al procesar la reserva:', err, reservation);
        return false;
      }
    });
    
    return reservation;
  };

  // Obtener información de reserva para un día y habitación específicos
  const getReservationInfo = (day, roomId) => {
    return isDayReserved(day, roomId);
  };

  // Función para calcular la duración de una reserva en días
  const getReservationSpan = (reservation) => {
    try {
      // Obtener campos de fecha - pueden usar fechaEntrada/fechaSalida o checkIn/checkOut
      let checkInField = reservation.fechaEntrada || reservation.checkIn;
      let checkOutField = reservation.fechaSalida || reservation.checkOut;
      
      if (!checkInField || !checkOutField) {
        console.log("Reserva sin fechas completas:", reservation);
        return 1;
      }
      
      // Convertir a objetos Date si son timestamps o strings
      let checkIn, checkOut;
      
      if (typeof checkInField === 'object' && checkInField.seconds) {
        checkIn = new Date(checkInField.seconds * 1000);
      } else if (checkInField instanceof Date) {
        checkIn = checkInField;
      } else if (typeof checkInField === 'string') {
        checkIn = new Date(checkInField);
      }
      
      if (typeof checkOutField === 'object' && checkOutField.seconds) {
        checkOut = new Date(checkOutField.seconds * 1000);
      } else if (checkOutField instanceof Date) {
        checkOut = checkOutField;
      } else if (typeof checkOutField === 'string') {
        checkOut = new Date(checkOutField);
      }
      
      // Normalizar horas para comparación
      checkIn.setHours(0, 0, 0, 0);
      checkOut.setHours(0, 0, 0, 0);
      
      // Calcular diferencia en días
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Verificar si la reserva cruza el límite del mes
      const reservationStartsInCurrentMonth = 
        checkIn.getMonth() === currentMonth && 
        checkIn.getFullYear() === currentYear;
        
      const reservationEndsInNextMonth = 
        checkOut.getMonth() !== currentMonth || 
        checkOut.getFullYear() !== currentYear;
        
      console.log('Duración de reserva:', {
        reservationId: reservation.id,
        name: reservation.nombre || reservation.name,
        checkIn: checkIn.toISOString().split('T')[0],
        checkOut: checkOut.toISOString().split('T')[0],
        diffDays: diffDays,
        startsInCurrentMonth: reservationStartsInCurrentMonth,
        endsInNextMonth: reservationEndsInNextMonth
      });
      
      return diffDays;
    } catch (err) {
      console.error("Error al calcular duración de reserva:", err);
      return 1;
    }
  };
  
  // Función para comprobar si es el primer día de la reserva
  const isFirstDayOfReservation = (day, roomId, reservation) => {
    if (!reservation) return false;
    
    try {
      // Obtener campo de fecha de entrada
      let checkInField = reservation.fechaEntrada || reservation.checkIn;
      
      if (!checkInField) return false;
      
      // Convertir a objeto Date si es timestamp o string
      let checkIn;
      
      if (typeof checkInField === 'object' && checkInField.seconds) {
        checkIn = new Date(checkInField.seconds * 1000);
      } else if (checkInField instanceof Date) {
        checkIn = checkInField;
      } else if (typeof checkInField === 'string') {
        checkIn = new Date(checkInField);
      }
      
      // Crear fecha del día actual
      const currentDate = new Date(currentYear, currentMonth, day);
      
      // Normalizar horas para comparación
      checkIn.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);
      
      // Comprobar si es el primer día
      return currentDate.getTime() === checkIn.getTime();
    } catch (err) {
      console.error("Error al comprobar primer día de reserva:", err);
      return false;
    }
  };

  // Generar un código de reserva único de 6 dígitos
  const generateReservationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Manejar clic en una celda del calendario
  const handleCellClick = async (day, roomId) => {
    // Sugerencia: Agregar validaciones para evitar problemas durante carga
    if (loading) return;

    // Obtener información de la reserva para esta celda (en lugar de usar calendar)
    const reservation = getReservationInfo(day, roomId);
    
    if (multiBlockMode) {
      // Manejar selección en modo bloqueo múltiple
      handleRoomSelection(roomId);
      return;
    }
    
    if (reservation) {
      // Si ya hay una reserva, verificar si es parte de una reserva múltiple
      const reservationCode = reservation.codigoReserva || reservation.reservationCode;
      
      // Buscar todas las reservas con el mismo código de reserva
      const relatedReservations = reservations.filter(res => 
        (res.codigoReserva || res.reservationCode) === reservationCode
      );
      
      console.log(`Encontradas ${relatedReservations.length} habitaciones con el código de reserva ${reservationCode}`);
      
      // Verificar si hay registros duplicados (misma habitación) y filtrarlos
      const uniqueRelatedReservations = [];
      const roomIdSet = new Set();
      
      relatedReservations.forEach(res => {
        const roomId = (res.habitacionId || res.roomId).toString();
        // Solo agregar si no hemos visto esta habitación antes
        if (!roomIdSet.has(roomId)) {
          roomIdSet.add(roomId);
          uniqueRelatedReservations.push(res);
        } else {
          console.warn(`Reserva duplicada detectada para habitación ${roomId} con código ${reservationCode}`);
        }
      });
      
      console.log(`Después de filtrar duplicados: ${uniqueRelatedReservations.length} habitaciones únicas`);
      
      setModalMode('edit');
      setEditingReservation({
        ...reservation,
        id: reservation.id || '',
        // Añadir información de que es una reserva múltiple
        isMultipleReservation: uniqueRelatedReservations.length > 1,
        relatedReservationsIds: uniqueRelatedReservations.map(res => res.id)
      });
      
      // Formatear fechas para el formulario
      const checkInDate = reservation.fechaEntrada instanceof Date 
        ? reservation.fechaEntrada 
        : new Date(reservation.fechaEntrada.seconds * 1000);
      
      const checkOutDate = reservation.fechaSalida instanceof Date 
        ? reservation.fechaSalida 
        : new Date(reservation.fechaSalida.seconds * 1000);
      
      // Formatear fechas a YYYY-MM-DD para los inputs de fecha
      const checkInStr = `${checkInDate.getFullYear()}-${(checkInDate.getMonth() + 1).toString().padStart(2, '0')}-${checkInDate.getDate().toString().padStart(2, '0')}`;
      const checkOutStr = `${checkOutDate.getFullYear()}-${(checkOutDate.getMonth() + 1).toString().padStart(2, '0')}-${checkOutDate.getDate().toString().padStart(2, '0')}`;
      
      // Calcular duración en días
      const diffTime = Math.abs(checkOutDate - checkInDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Recolectar todas las habitaciones relacionadas y sus ocupantes
      const selectedRoomsArray = relatedReservations.map(res => (res.habitacionId || res.roomId).toString());
      const occupancyObj = {};
      
      // Asegurarnos que no hay duplicados en selectedRoomsArray
      const uniqueSelectedRooms = [...new Set(selectedRoomsArray)];
      console.log(`Habitaciones en la reserva múltiple (código ${reservationCode}):`, uniqueSelectedRooms);
      
      relatedReservations.forEach(res => {
        const roomId = (res.habitacionId || res.roomId).toString();
        const occupancy = res.personasEnHabitacion || res.guests || 1;
        occupancyObj[roomId] = occupancy;
      });
      
      // Actualizar el estado de las habitaciones seleccionadas
      setSelectedRooms(uniqueSelectedRooms);
      setRoomOccupancy(occupancyObj);
      
      // IMPORTANTE: Obtener el precio por noche de los campos guardados específicamente
      // Intentar obtener el precio por noche en el siguiente orden de preferencia:
      // 1. Campo específico precioPorNoche o pricePerNight
      // 2. Campo precioHabitacion o roomPrice 
      // 3. Calcular a partir del precio total
      
      let pricePerNight = null;
      
      if (reservation.precioPorNoche) {
        pricePerNight = reservation.precioPorNoche;
        console.log("Cargando precio por noche desde campo 'precioPorNoche':", pricePerNight);
      } else if (reservation.pricePerNight) {
        pricePerNight = reservation.pricePerNight;
        console.log("Cargando precio por noche desde campo 'pricePerNight':", pricePerNight);
      } else if (reservation.precioHabitacion) {
        pricePerNight = reservation.precioHabitacion;
        console.log("Cargando precio por noche desde campo 'precioHabitacion':", pricePerNight);
      } else if (reservation.roomPrice) {
        pricePerNight = reservation.roomPrice;
        console.log("Cargando precio por noche desde campo 'roomPrice':", pricePerNight);
      } else {
        // Si no podemos encontrar ningún precio específico, calcular desde el precio total
        const totalPrice = reservation.precioTotal || reservation.totalPrice || 0;
        pricePerNight = diffDays > 0 ? Math.round(totalPrice / diffDays) : 0;
        console.log("Precio por noche calculado desde precioTotal:", pricePerNight);
      }
      
      console.log("Precio por noche final para formulario:", pricePerNight);
      
      // Objeto completo para depuración
      console.log("Valores importantes:", {
        precioPorNoche: reservation.precioPorNoche,
        pricePerNight: reservation.pricePerNight,
        precioHabitacion: reservation.precioHabitacion,
        roomPrice: reservation.roomPrice,
        precioTotal: reservation.precioTotal,
        totalPrice: reservation.totalPrice,
        diffDays: diffDays,
        pricePerNightFinal: pricePerNight
      });
      
      // Obtener datos de la reserva principal
      setNewReservation({
        name: reservation.nombre || reservation.name || '',
        email: reservation.email || '',
        phone: reservation.telefono || reservation.phone || '',
        checkIn: checkInStr,
        checkOut: checkOutStr,
        rooms: [], // Ya no necesitamos esto porque usamos selectedRooms
        reservationCode: reservationCode,
        duration: diffDays,
        totalPrice: reservation.precioTotal || reservation.totalPrice || 0,
        pricePerNight: pricePerNight, // Usar el precio guardado en la reserva
        calendarEventId: reservation.calendarEventId || '',
        syncDate: reservation.fechaSincronizacion || reservation.syncDate || '',
        syncWithCalendar: reservation.sincronizadoCalendario || reservation.syncWithCalendar || false,
        tipoPago: reservation.tipoPago || reservation.metodoPago || 'Efectivo',
        sendEmail: false // No enviar correo al editar
      });
      
      // Calcular el precio total basado en todas las habitaciones
      // NOTA: No recalcular aquí el precio para no sobreescribir el personalizado
      // Solo necesitamos actualizar el precio total en el formulario
      const totalCalculated = pricePerNight * diffDays * uniqueSelectedRooms.length;
      console.log(`Precio total calculado: ${pricePerNight} × ${diffDays} días × ${uniqueSelectedRooms.length} habitaciones = ${totalCalculated}`);
      
    } else {
      // Si no hay reserva, abrir modal para agregar
      const roomData = rooms.find(r => r.id.toString() === roomId.toString());
      
      // Usar el objeto Date pero formatearlo sin el problema de zona horaria
      const checkInDate = new Date(currentYear, currentMonth, day);
      const checkOutDate = new Date(currentYear, currentMonth, day);
      checkOutDate.setDate(checkOutDate.getDate() + 1);
      
      // Formatear fechas manualmente para evitar problemas de zona horaria
      const checkInStr = `${checkInDate.getFullYear()}-${(checkInDate.getMonth() + 1).toString().padStart(2, '0')}-${checkInDate.getDate().toString().padStart(2, '0')}`;
      const checkOutStr = `${checkOutDate.getFullYear()}-${(checkOutDate.getMonth() + 1).toString().padStart(2, '0')}-${checkOutDate.getDate().toString().padStart(2, '0')}`;
      
      // Calcular duración y precios
      const duration = 1; // Por defecto 1 día
      
      // Preparar objeto inicial de reserva (sin precio aún)
      const initialReservation = {
        name: '',
        email: '',
        phone: '',
        checkIn: checkInStr,
        checkOut: checkOutStr,
        rooms: [],
        reservationCode: generateReservationCode(),
        duration: duration,
        totalPrice: 0, // Se establecerá más adelante
        pricePerNight: 0, // Agregar precio por noche
        calendarEventId: '',
        syncDate: '',
        syncWithCalendar: false,
        tipoPago: 'Efectivo',
        sendEmail: true
      };
      
      setModalMode('add');
      setEditingReservation(null);
      setNewReservation(initialReservation);
      
      // Inicializar habitación seleccionada con la habitación donde se hizo clic
      setSelectedRooms([roomId.toString()]);
      
      // Inicializar ocupación con 1 persona
      setRoomOccupancy({
        [roomId.toString()]: 1
      });

      // Calcular precio total basado en la habitación seleccionada
      calculateTotalPrice([roomId.toString()], { [roomId.toString()]: 1 }, duration);
    }
    
    setSelectedCell({ day, roomId });
    setIsModalOpen(true);
  };

  // Manejar cambios en el formulario
  const handleFormChange = async (e) => {
    const { name, value, type, checked } = e.target;
    
    // Actualización especial para fechas
    if (name === 'checkIn' || name === 'checkOut') {
      const newValue = { ...newReservation, [name]: value };
      
      // Recalcular la duración si ambas fechas están presentes
      if (newValue.checkIn && newValue.checkOut) {
        // Crear fechas con hora fija para evitar problemas de zona horaria
        const checkIn = new Date(newValue.checkIn + 'T12:00:00');
        const checkOut = new Date(newValue.checkOut + 'T12:00:00');
        
        // Calcular la diferencia en días
        const diffTime = Math.abs(checkOut - checkIn);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Actualizar duración
        newValue.duration = diffDays;
        
        // Actualizar primero el estado con la nueva duración
      setNewReservation(newValue);
      
        // Recalcular el precio total con las habitaciones seleccionadas y la nueva duración
        if (selectedRooms.length > 0) {
          calculateTotalPrice(selectedRooms, roomOccupancy, diffDays);
        }
      } else {
        setNewReservation(newValue);
      }
    } else if (name === 'pricePerNight') {
      // Si el usuario edita manualmente el precio por noche
      const pricePerNight = parseInt(value);
      const totalPrice = pricePerNight * newReservation.duration * selectedRooms.length;
      setNewReservation(prev => ({
        ...prev,
        pricePerNight: pricePerNight,
        totalPrice: totalPrice
      }));
    } else if (name === 'syncWithCalendar' || name === 'sendEmail') {
      // Manejo especial para checkboxes
      setNewReservation({
        ...newReservation,
        [name]: checked
      });
    } else {
      // Actualización normal para otros campos
      setNewReservation(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Calcular precio de habitación según ID y número de huéspedes
  const calculateRoomPrice = async (roomId, guests) => {
    // Buscar la habitación seleccionada
    const selectedRoom = rooms.find(h => h.id.toString() === roomId.toString());
    
    if (!selectedRoom) {
      console.log("Habitación no encontrada:", roomId);
      return 33000; // Valor por defecto si no se encuentra
    }

    try {
      // Obtener el precio por temporada si estamos creando una nueva reserva
      if (newReservation && newReservation.checkIn) {
        const fechaEntrada = newReservation.checkIn;
        console.log(`Consultando precio de temporada para fecha: ${fechaEntrada}, habitación: ${roomId}, huéspedes: ${guests}`);
        
        // Usar la función obtenerPrecioPorOcupacionYFecha para obtener el precio considerando temporadas
        const precioTemporada = await obtenerPrecioPorOcupacionYFecha(
          selectedRoom,
          guests,
          fechaEntrada
        );
        
        if (precioTemporada) {
          console.log(`Encontrado precio de temporada: ${precioTemporada}`);
          return parseInt(precioTemporada);
        }
      }
      
      // Si no hay precio de temporada o hubo un error, continuar con la lógica anterior
      // Verificar si hay precios específicos por ocupación
      if (selectedRoom.preciosPorOcupacion && selectedRoom.preciosPorOcupacion[guests]) {
        console.log(`Usando precio específico para ${guests} personas:`, selectedRoom.preciosPorOcupacion[guests]);
        return parseInt(selectedRoom.preciosPorOcupacion[guests]);
      }
      
      // Si no hay precio específico para este número de huéspedes, usar el precio base
      console.log(`No se encontró precio específico para ${guests} personas, usando precio base:`, selectedRoom.precio);
      return parseInt(selectedRoom.precio || 33000);
    } catch (error) {
      console.error("Error al calcular precio de temporada:", error);
      
      // En caso de error, usar los precios normales
      if (selectedRoom.preciosPorOcupacion && selectedRoom.preciosPorOcupacion[guests]) {
        return parseInt(selectedRoom.preciosPorOcupacion[guests]);
      }
      return parseInt(selectedRoom.precio || 33000);
    }
  };

  // Manejar envío del formulario
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Si ya se está enviando, no hacer nada
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      // Convertir fechas a timestamp de Firestore con corrección de zona horaria
      const checkInDate = new Date(newReservation.checkIn + 'T12:00:00');
      const checkOutDate = new Date(newReservation.checkOut + 'T12:00:00');
      
      // Verificar que haya al menos una habitación seleccionada
      if (selectedRooms.length === 0) {
        setError('Por favor, seleccione al menos una habitación');
        return;
      }
      
      // Verificar si ya existe una reserva para este período y habitaciones - solo para modo agregar
      if (modalMode === 'add') {
        const existingReservations = reservations.filter(res => {
        const resCheckIn = res.fechaEntrada || res.checkIn;
        const resCheckOut = res.fechaSalida || res.checkOut;
        const resRoomId = res.habitacionId || res.roomId;
        
        // Convertir fechas a objetos Date si son timestamps
        let checkIn, checkOut;
        if (typeof resCheckIn === 'object' && resCheckIn.seconds) {
          checkIn = new Date(resCheckIn.seconds * 1000);
        } else if (resCheckIn instanceof Date) {
          checkIn = resCheckIn;
        } else {
          checkIn = new Date(resCheckIn);
        }
        
        if (typeof resCheckOut === 'object' && resCheckOut.seconds) {
          checkOut = new Date(resCheckOut.seconds * 1000);
        } else if (resCheckOut instanceof Date) {
          checkOut = resCheckOut;
        } else {
          checkOut = new Date(resCheckOut);
        }
        
        // Normalizar horas para comparación
        checkIn.setHours(12, 0, 0, 0);
        checkOut.setHours(12, 0, 0, 0);
        checkInDate.setHours(12, 0, 0, 0);
        checkOutDate.setHours(12, 0, 0, 0);
        
        // Verificar si hay solapamiento de fechas y misma habitación
        return (
            selectedRooms.includes(resRoomId.toString()) &&
          ((checkInDate >= checkIn && checkInDate < checkOut) ||
           (checkOutDate > checkIn && checkOutDate <= checkOut) ||
           (checkInDate <= checkIn && checkOutDate >= checkOut))
        );
      });
      
        if (existingReservations.length > 0) {
          setError('Ya existe una reserva para este período en alguna de las habitaciones seleccionadas.');
        return;
        }
      }
      
      // Comprobar si es una reserva vacía (día bloqueado)
      const isBlockedDay = !newReservation.name.trim() && !newReservation.email.trim() && !newReservation.phone.trim();
      
      // Guardar el precio por noche personalizado (si ha sido modificado)
      const customPricePerNight = parseInt(newReservation.pricePerNight) || 0;
      console.log("Guardando precio por noche personalizado:", customPricePerNight);
      
      if (modalMode === 'add') {
        // Crear reservas para cada habitación
        const reservationPromises = selectedRooms.map(async (roomId) => {
          const occupancy = roomOccupancy[roomId] || 1;
          // Usar el precio personalizado introducido por el usuario
          let roomPrice = customPricePerNight;
          
          // Si no hay un precio personalizado, calcularlo
          if (roomPrice <= 0) {
            roomPrice = await calculateRoomPrice(roomId, occupancy);
          }
          
          console.log(`Habitación ${roomId}: Guardando con precio ${roomPrice}`);
          
      const reservationData = {
        nombre: isBlockedDay ? "Bloqueado" : newReservation.name,
        name: isBlockedDay ? "Bloqueado" : newReservation.name,
        email: isBlockedDay ? "bloqueado@sistema.interno" : newReservation.email,
        telefono: isBlockedDay ? "-" : newReservation.phone,
        phone: isBlockedDay ? "-" : newReservation.phone,
        fechaEntrada: checkInDate,
        checkIn: checkInDate,
        fechaSalida: checkOutDate,
        checkOut: checkOutDate,
        fechaCreacion: new Date(),
            habitacionId: parseInt(roomId),
            roomId: parseInt(roomId),
            personasEnHabitacion: occupancy,
            guests: occupancy,
        duracionEstancia: parseInt(newReservation.duration),
        duration: parseInt(newReservation.duration),
            precioHabitacion: roomPrice, // Usar el precio personalizado
            roomPrice: roomPrice, // Usar el precio personalizado
            precioTotal: roomPrice * newReservation.duration,
            totalPrice: roomPrice * newReservation.duration,
        codigoReserva: newReservation.reservationCode,
        reservationCode: newReservation.reservationCode,
        calendarEventId: newReservation.calendarEventId || "",
        fechaSincronizacion: newReservation.syncDate || "",
        syncDate: newReservation.syncDate || "",
        sincronizadoCalendario: newReservation.syncWithCalendar || false,
        syncWithCalendar: newReservation.syncWithCalendar || false,
        tipoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
            metodoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
            status: isBlockedDay ? 'blocked' : 'confirmed',
            // Guardar explícitamente el precio por noche como campo adicional para futuras ediciones
            precioPorNoche: roomPrice,
            pricePerNight: roomPrice
      };
      
          return addDoc(collection(db, 'reservas'), reservationData);
        });
        
        // Esperar a que todas las reservas se guarden
        const results = await Promise.all(reservationPromises);
        
        // Enviar correo si está habilitado
        if (newReservation.sendEmail && !isBlockedDay && newReservation.email) {
          try {
            // Preparar datos para el correo
            const reservaParaCorreo = {
              id: results[0].id, // Usar el ID de la primera reserva
              nombre: newReservation.name,
              email: newReservation.email,
              telefono: newReservation.phone,
              fechaEntrada: newReservation.checkIn,
              fechaSalida: newReservation.checkOut,
              habitaciones: await Promise.all(selectedRooms.map(async (roomId) => {
                const room = rooms.find(r => r.id.toString() === roomId);
                let roomPrice = customPricePerNight;
                if (roomPrice <= 0) {
                  roomPrice = await calculateRoomPrice(roomId, roomOccupancy[roomId] || 1);
                }
                return {
                  id: roomId,
                  nombre: room.nombre || room.name || `Habitación ${roomId}`,
                  name: room.nombre || room.name || `Habitación ${roomId}`,
                  tipo: room.tipo,
                  capacidad: room.capacidad,
                  precio: roomPrice,
                  preciosPorOcupacion: room.preciosPorOcupacion || {}
                };
              })),
              distribucionPersonas: Object.fromEntries(
                selectedRooms.map(roomId => [roomId, roomOccupancy[roomId] || 1])
              ),
              duracionEstancia: parseInt(newReservation.duration),
              precioTotal: newReservation.totalPrice,
              codigoReserva: newReservation.reservationCode
            };
            
            await enviarCorreoConfirmacion(reservaParaCorreo, false);
          } catch (emailErr) {
            console.error("Error al enviar correo:", emailErr);
          }
        }
        
        setSuccess(`Reserva creada con éxito en ${selectedRooms.length} habitación(es). Código: ${newReservation.reservationCode}`);
      } else if (modalMode === 'edit') {
        // Si es edición, actualizar todas las reservas relacionadas
        
        // Verificar si hay cambios en las habitaciones seleccionadas
        const originalRoomIds = [];
        
        // Correctamente mapear las habitaciones originales de cada reserva relacionada
        if (editingReservation.isMultipleReservation && editingReservation.relatedReservationsIds) {
          // Para cada ID de reserva relacionada, obtener su habitación
          for (const reservationId of editingReservation.relatedReservationsIds) {
            const reservaDoc = await getDoc(doc(db, 'reservas', reservationId));
            if (reservaDoc.exists()) {
              const reservaData = reservaDoc.data();
              const roomId = (reservaData.habitacionId || reservaData.roomId).toString();
              originalRoomIds.push(roomId);
            }
          }
        } else {
          // Si es una reserva simple
          originalRoomIds.push((editingReservation.habitacionId || editingReservation.roomId).toString());
        }
        
        console.log('Habitaciones originales:', originalRoomIds);
        console.log('Habitaciones seleccionadas:', selectedRooms);
        
        // Habitaciones a agregar (están en selectedRooms pero no en originalRoomIds)
        const roomsToAdd = selectedRooms.filter(roomId => 
          !originalRoomIds.includes(roomId)
        );
        
        // Habitaciones a eliminar (están en originalRoomIds pero no en selectedRooms)
        const roomsToDelete = originalRoomIds.filter(roomId => 
          !selectedRooms.includes(roomId)
        );
        
        console.log('Habitaciones a agregar:', roomsToAdd);
        console.log('Habitaciones a eliminar:', roomsToDelete);
        
        // 1. Actualizar las reservas existentes
        const updatePromises = [];
        
        if (editingReservation.isMultipleReservation) {
          // Si es una reserva múltiple, actualizar todas las relacionadas
          const reservationsToUpdate = [];
          
          // Para cada ID de reserva relacionada
          for (const reservationId of editingReservation.relatedReservationsIds) {
            // Obtener la reserva para conocer su habitación
            const reservaDoc = await getDoc(doc(db, 'reservas', reservationId));
            
            if (reservaDoc.exists()) {
              const reservaData = reservaDoc.data();
              const roomId = (reservaData.habitacionId || reservaData.roomId).toString();
              
              // Solo actualizar si la habitación sigue seleccionada y no está en roomsToDelete
              if (selectedRooms.includes(roomId) && !roomsToDelete.includes(roomId)) {
                const occupancy = roomOccupancy[roomId] || 1;
                // Usar el precio personalizado introducido por el usuario
                let roomPrice = customPricePerNight;
                
                // Si no hay un precio personalizado, calcularlo
                if (roomPrice <= 0) {
                  roomPrice = await calculateRoomPrice(roomId, occupancy);
                }
                
                console.log(`Actualizando habitación ${roomId} con precio ${roomPrice}`);
                
                const updateData = {
                  nombre: isBlockedDay ? "Bloqueado" : newReservation.name,
                  name: isBlockedDay ? "Bloqueado" : newReservation.name,
                  email: isBlockedDay ? "bloqueado@sistema.interno" : newReservation.email,
                  telefono: isBlockedDay ? "-" : newReservation.phone,
                  phone: isBlockedDay ? "-" : newReservation.phone,
                  fechaEntrada: checkInDate,
                  checkIn: checkInDate,
                  fechaSalida: checkOutDate,
                  checkOut: checkOutDate,
                  personasEnHabitacion: occupancy,
                  guests: occupancy,
                  duracionEstancia: parseInt(newReservation.duration),
                  duration: parseInt(newReservation.duration),
                  precioHabitacion: roomPrice,
                  roomPrice: roomPrice,
                  precioTotal: roomPrice * newReservation.duration,
                  totalPrice: roomPrice * newReservation.duration,
                  calendarEventId: newReservation.calendarEventId || "",
                  fechaSincronizacion: newReservation.syncDate || "",
                  syncDate: newReservation.syncDate || "",
                  sincronizadoCalendario: newReservation.syncWithCalendar || false,
                  syncWithCalendar: newReservation.syncWithCalendar || false,
                  tipoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
                  metodoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
                  status: isBlockedDay ? 'blocked' : 'confirmed',
                  // Guardar explícitamente el precio por noche como campo adicional
                  precioPorNoche: roomPrice,
                  pricePerNight: roomPrice
                };
                
                reservationsToUpdate.push({ id: reservationId, roomId: roomId });
                updatePromises.push(updateDoc(doc(db, 'reservas', reservationId), updateData));
              }
            }
          }
          
          console.log('Reservas a actualizar:', reservationsToUpdate);
        } else {
          // Si es una reserva simple, solo actualizar esta reserva
          if (selectedRooms.includes((editingReservation.habitacionId || editingReservation.roomId).toString())) {
            const roomId = (editingReservation.habitacionId || editingReservation.roomId).toString();
            const occupancy = roomOccupancy[roomId] || 1;
            
            // Usar el precio personalizado introducido por el usuario
            let roomPrice = customPricePerNight;
            
            // Si no hay un precio personalizado, calcularlo
            if (roomPrice <= 0) {
              roomPrice = await calculateRoomPrice(roomId, occupancy);
            }
            
            console.log(`Actualizando reserva simple para habitación ${roomId} con precio ${roomPrice}`);
            
            const updateData = {
              nombre: isBlockedDay ? "Bloqueado" : newReservation.name,
              name: isBlockedDay ? "Bloqueado" : newReservation.name,
              email: isBlockedDay ? "bloqueado@sistema.interno" : newReservation.email,
              telefono: isBlockedDay ? "-" : newReservation.phone,
              phone: isBlockedDay ? "-" : newReservation.phone,
              fechaEntrada: checkInDate,
              checkIn: checkInDate,
              fechaSalida: checkOutDate,
              checkOut: checkOutDate,
              personasEnHabitacion: occupancy,
              guests: occupancy,
              duracionEstancia: parseInt(newReservation.duration),
              duration: parseInt(newReservation.duration),
              precioHabitacion: roomPrice,
              roomPrice: roomPrice,
              precioTotal: roomPrice * newReservation.duration,
              totalPrice: roomPrice * newReservation.duration,
              calendarEventId: newReservation.calendarEventId || "",
              fechaSincronizacion: newReservation.syncDate || "",
              syncDate: newReservation.syncDate || "",
              sincronizadoCalendario: newReservation.syncWithCalendar || false,
              syncWithCalendar: newReservation.syncWithCalendar || false,
              tipoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
              metodoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
              status: isBlockedDay ? 'blocked' : 'confirmed',
              // Guardar explícitamente el precio por noche como campo adicional
              precioPorNoche: roomPrice,
              pricePerNight: roomPrice
            };
            
            updatePromises.push(updateDoc(doc(db, 'reservas', editingReservation.id), updateData));
          }
        }
        
        // 2. Crear nuevas reservas para las habitaciones agregadas
        const newReservationsToAdd = [];
        for (const roomId of roomsToAdd) {
          const occupancy = roomOccupancy[roomId] || 1;
          
          // Usar el precio personalizado introducido por el usuario
          let roomPrice = customPricePerNight;
          
          // Si no hay un precio personalizado, calcularlo
          if (roomPrice <= 0) {
            roomPrice = await calculateRoomPrice(roomId, occupancy);
          }
          
          console.log(`Agregando nueva habitación ${roomId} con precio ${roomPrice}`);
          
          const reservationData = {
            nombre: isBlockedDay ? "Bloqueado" : newReservation.name,
            name: isBlockedDay ? "Bloqueado" : newReservation.name,
            email: isBlockedDay ? "bloqueado@sistema.interno" : newReservation.email,
            telefono: isBlockedDay ? "-" : newReservation.phone,
            phone: isBlockedDay ? "-" : newReservation.phone,
            fechaEntrada: checkInDate,
            checkIn: checkInDate,
            fechaSalida: checkOutDate,
            checkOut: checkOutDate,
            fechaCreacion: new Date(),
            habitacionId: parseInt(roomId),
            roomId: parseInt(roomId),
            personasEnHabitacion: occupancy,
            guests: occupancy,
            duracionEstancia: parseInt(newReservation.duration),
            duration: parseInt(newReservation.duration),
            precioHabitacion: roomPrice,
            roomPrice: roomPrice,
            precioTotal: roomPrice * newReservation.duration,
            totalPrice: roomPrice * newReservation.duration,
            codigoReserva: newReservation.reservationCode,
            reservationCode: newReservation.reservationCode,
            calendarEventId: newReservation.calendarEventId || "",
            fechaSincronizacion: newReservation.syncDate || "",
            syncDate: newReservation.syncDate || "",
            sincronizadoCalendario: newReservation.syncWithCalendar || false,
            syncWithCalendar: newReservation.syncWithCalendar || false,
            tipoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
            metodoPago: isBlockedDay ? "N/A" : newReservation.tipoPago || "Efectivo",
            status: isBlockedDay ? 'blocked' : 'confirmed',
            // Guardar explícitamente el precio por noche
            precioPorNoche: roomPrice,
            pricePerNight: roomPrice
          };
          
          newReservationsToAdd.push({ roomId: roomId });
          updatePromises.push(addDoc(collection(db, 'reservas'), reservationData));
        }
        
        console.log('Nuevas reservas a agregar:', newReservationsToAdd);
        
        // 3. Eliminar las reservas de habitaciones que ya no están seleccionadas
        const reservationsToDelete = [];
        if (editingReservation.isMultipleReservation) {
          // Para cada ID de reserva relacionada
          for (const reservationId of editingReservation.relatedReservationsIds) {
            const reservaDoc = await getDoc(doc(db, 'reservas', reservationId));
            
            if (reservaDoc.exists()) {
              const reservaData = reservaDoc.data();
              const roomId = (reservaData.habitacionId || reservaData.roomId).toString();
              
              // Si la habitación ya no está seleccionada o está en roomsToDelete, eliminar la reserva
              if (!selectedRooms.includes(roomId) || roomsToDelete.includes(roomId)) {
                reservationsToDelete.push({ id: reservationId, roomId: roomId });
                updatePromises.push(deleteDoc(doc(db, 'reservas', reservationId)));
              }
            }
          }
        } else if (!selectedRooms.includes((editingReservation.habitacionId || editingReservation.roomId).toString())) {
          // Si es una reserva simple y la habitación original ya no está seleccionada, eliminarla
          reservationsToDelete.push({ id: editingReservation.id, roomId: (editingReservation.habitacionId || editingReservation.roomId).toString() });
          updatePromises.push(deleteDoc(doc(db, 'reservas', editingReservation.id)));
        }
        
        console.log('Reservas a eliminar:', reservationsToDelete);
        
        // Esperar a que se completen todas las operaciones
        await Promise.all(updatePromises);
        
        setSuccess(`Reserva actualizada con éxito en ${selectedRooms.length} habitación(es). Código: ${newReservation.reservationCode}`);
      }
      
      // Recargar reservaciones
      await fetchReservationsForMonth(currentMonth, currentYear);
      
      // Cerrar modal
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error al guardar reserva:", err);
      setError(`Error al guardar la reserva: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar eliminación de reserva
  const handleDeleteReservation = async () => {
    try {
      // Verificar si es una reserva múltiple
      if (editingReservation.isMultipleReservation) {
        // Eliminar todas las reservas relacionadas
        const deletePromises = editingReservation.relatedReservationsIds.map(id => 
          deleteDoc(doc(db, 'reservas', id))
        );
        
        await Promise.all(deletePromises);
        
        setSuccess(`Se han eliminado ${editingReservation.relatedReservationsIds.length} reservas asociadas al código ${newReservation.reservationCode}`);
      } else {
        // Eliminar solo la reserva actual
      await deleteDoc(doc(db, 'reservas', editingReservation.id));
      
      setSuccess("Reserva eliminada con éxito.");
      }
      
      // Recargar reservaciones
      await fetchReservationsForMonth(currentMonth, currentYear);
      
      // Cerrar modal
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error al eliminar reserva:", err);
      setError("Error al eliminar la reserva. Por favor, intente nuevamente.");
    }
  };

  // Cerrar mensajes
  const handleCloseMessage = () => {
    setError(null);
    setSuccess(null);
  };

  // Función para crear una reserva de prueba
  const createTestReservation = async () => {
    try {
      setLoading(true);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayStr = today.toISOString().split('T')[0];
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      
      console.log("Preparando reserva de prueba para guardar en Firebase...");
      console.log(`Fechas de reserva: ${todayStr} hasta ${tomorrowStr}`);
      
      // Duración y precios calculados
      const duration = 1;
      const roomPrice = 33000;
      const totalPrice = roomPrice * duration;
      const roomId = 1;
      
      const reservationData = {
        // Usar ambos formatos de campo para compatibilidad
        nombre: "Reserva de Prueba",
        name: "Reserva de Prueba",
        email: "test@example.com",
        telefono: "123456789",
        phone: "123456789",
        fechaEntrada: today,
        checkIn: today,
        fechaSalida: tomorrow,
        checkOut: tomorrow,
        fechaCreacion: new Date(),
        habitacionId: roomId,
        roomId: roomId,
        personasEnHabitacion: 2,
        guests: 2,
        duracionEstancia: duration,
        duration: duration,
        precioHabitacion: roomPrice,
        roomPrice: roomPrice,
        precioTotal: totalPrice,
        totalPrice: totalPrice,
        codigoReserva: Math.floor(100000 + Math.random() * 900000).toString(),
        reservationCode: Math.floor(100000 + Math.random() * 900000).toString(),
        calendarEventId: "",
        fechaSincronizacion: "",
        syncDate: "",
        sincronizadoCalendario: false,
        syncWithCalendar: false,
        status: 'confirmed'
      };
      
      console.log("Datos de reserva a guardar:", reservationData);
      
      // Referencia a la colección de reservas
      const reservasCollectionRef = collection(db, 'reservas');
      console.log("Referencia a colección obtenida:", reservasCollectionRef);
      
      // Intentar guardar la reserva
      console.log("Intentando guardar reserva en Firebase...");
      const docRef = await addDoc(reservasCollectionRef, reservationData);
      
      console.log("Reserva guardada exitosamente con ID:", docRef.id);
      const checkInStr = today.toISOString().split('T')[0];
      const checkOutStr = tomorrow.toISOString().split('T')[0];
      setSuccess(`Reserva de prueba creada con éxito para ${checkInStr} hasta ${checkOutStr} en la habitación ${roomId}. ID: ${docRef.id}`);
      
      // Agregar manualmente al estado local para asegurar que se muestre inmediatamente
      const newReservation = { 
        id: docRef.id, 
        ...reservationData 
      };
      setReservations(prev => [...prev, newReservation]);
      
      // Verificar que se haya guardado correctamente
      console.log("Verificando que la reserva se haya guardado...");
      const savedDoc = await getDoc(doc(db, 'reservas', docRef.id));
      
      if (savedDoc.exists()) {
        console.log("Reserva verificada en la base de datos:", savedDoc.data());
      } else {
        console.error("¡Advertencia! La reserva no se encontró en la base de datos después de guardarla");
        setError("La reserva se guardó pero no se pudo verificar en la base de datos. Puede haber problemas de permisos.");
      }
      
      // Verificar si la reserva aparecería en el calendario
      const currentDate = new Date();
      if (currentDate.getMonth() === currentMonth && currentDate.getFullYear() === currentYear) {
        console.log("Verificando si la reserva aparecería en el calendario...");
        const day = currentDate.getDate();
        const reservedDay = isDayReserved(day, roomId);
        console.log(`¿La reserva aparece para el día ${day}?`, !!reservedDay);
      }
      
    } catch (err) {
      console.error("Error al crear reserva de prueba:", err);
      setError(`Error al crear reserva de prueba: ${err.message}. Verifique la consola para más detalles.`);
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar la conexión a Firebase
  const checkFirebaseConnection = async () => {
    try {
      console.log("Verificando conexión a Firebase...");
      console.log("Configuración de Firebase:", db);
      
      // Intentar leer todas las colecciones
      const reservasCollectionRef = collection(db, 'reservas');
      const querySnapshot = await getDocs(reservasCollectionRef);
      
      console.log(`Conexión exitosa. Se encontraron ${querySnapshot.docs.length} documentos en 'reservas'`);
      
      // Mostrar los primeros 5 documentos
      const documents = querySnapshot.docs.slice(0, 5).map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("Muestra de documentos:", documents);
      
      setSuccess(`Conexión exitosa a Firebase. Se encontraron ${querySnapshot.docs.length} reservas.`);
      
      // Verificar si Firebase está inicializado correctamente
      if (!db) {
        setError("Error: La base de datos de Firebase no está inicializada correctamente.");
      }
      
      return true;
    } catch (err) {
      console.error("Error al verificar conexión a Firebase:", err);
      setError(`Error de conexión a Firebase: ${err.message}. Verifique la consola para más detalles.`);
      return false;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    // Handle Firebase Timestamp
    if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    
    // Handle Date objects
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleDateString();
    }
    
    return '';
  };

  const renderCellContent = (day, month, year) => {
    const cellDate = new Date(year, month, day);
    const dateString = cellDate.toISOString().split('T')[0];
    const reservationsForCell = reservations.filter(reservation => {
      let checkIn, checkOut;
      
      // Handle Firebase Timestamp for checkIn
      if (reservation.checkIn && typeof reservation.checkIn === 'object' && reservation.checkIn.seconds) {
        checkIn = new Date(reservation.checkIn.seconds * 1000);
      } else if (reservation.checkIn instanceof Date) {
        checkIn = reservation.checkIn;
      } else if (typeof reservation.checkIn === 'string') {
        checkIn = new Date(reservation.checkIn);
      }
      
      // Handle Firebase Timestamp for checkOut
      if (reservation.checkOut && typeof reservation.checkOut === 'object' && reservation.checkOut.seconds) {
        checkOut = new Date(reservation.checkOut.seconds * 1000);
      } else if (reservation.checkOut instanceof Date) {
        checkOut = reservation.checkOut;
      } else if (typeof reservation.checkOut === 'string') {
        checkOut = new Date(reservation.checkOut);
      }
      
      if (!checkIn || !checkOut) return false;
      
      const checkInDate = new Date(checkIn);
      checkInDate.setHours(0, 0, 0, 0);
      
      const checkOutDate = new Date(checkOut);
      checkOutDate.setHours(0, 0, 0, 0);
      
      cellDate.setHours(0, 0, 0, 0);
      
      return cellDate >= checkInDate && cellDate <= checkOutDate;
    });

    // ... existing code in renderCellContent ...
  };

  // Renderizar el calendario
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const today = new Date();
    const isCurrentMonth = today.getMonth() === currentMonth && today.getFullYear() === currentYear;
    
    // Usar días en orden natural (1 a fin de mes)
    const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    
    // El día actual (para resaltarlo)
    const currentDay = isCurrentMonth ? today.getDate() : null;
    
    // Crear un set local en lugar de usar estado para rastrear reservas renderizadas
    const renderedReservations = new Set();
    
    return (
      <div className="calendar-grid">
        <div className="calendar-row">
          {/* Celda vacía en la esquina superior izquierda */}
          <div className="calendar-cell corner-cell">Habitaciones</div>
          
          {/* Encabezados de días */}
          {calendarDays.map(day => {
            // Determinar el día de la semana (0 = domingo, 1 = lunes, etc.)
            const dayOfWeek = new Date(currentYear, currentMonth, day).getDay();
            const dayName = ['D', 'L', 'M', 'X', 'J', 'V', 'S'][dayOfWeek];
            
            // Destacar el día actual
            const isToday = isCurrentMonth && day === currentDay;
            
            return (
              <div 
                key={`day-${day}`} 
                className={`calendar-cell header-cell ${isToday ? 'today' : ''}`}
                id={isToday ? 'current-day-column' : ''}
              >
                <div>{day}</div>
                <small>{dayName}</small>
              </div>
            );
          })}
        </div>
        
        {/* Filas de habitaciones - ordenadas por ID */}
        {rooms
          .sort((a, b) => {
            const idA = parseInt(a.id);
            const idB = parseInt(b.id);
            return idA - idB;
          })
          .map(room => (
            <div key={`room-${room.id}`} className="calendar-row">
              {/* Nombre de habitación */}
              <div className="calendar-cell room-cell">
                <div className="room-name">Habitación {room.id}</div>
              </div>
              
              {/* Celdas de días para esta habitación */}
              {calendarDays.map(day => {
                const reservation = getReservationInfo(day, room.id);
                
                // Si no hay reserva o no es día de reserva, mostrar celda normal
                if (!reservation) {
                  return (
                    <div
                      key={`cell-${room.id}-${day}`}
                      className={`calendar-cell available ${isCurrentMonth && day === currentDay ? 'today' : ''}`}
                      onClick={() => handleCellClick(day, room.id)}
                    ></div>
                  );
                }
                
                // Comprobar si es el primer día de la reserva
                const isFirstDay = isFirstDayOfReservation(day, room.id, reservation);
                
                // Si no es el primer día, y ya hemos renderizado esta reserva, no mostrar nada
                const reservationKey = `${reservation.id}-${room.id}`;
                if (!isFirstDay && renderedReservations.has(reservationKey)) {
                  return null;
                }
                
                // Determinar si la reserva es el último día del mes
                const isLastDayOfMonth = day === daysInMonth;
                
                // Determinar si la reserva es el primer día del mes
                const isFirstDayOfMonth = day === 1;
                
                // Obtener las fechas de la reserva para análisis
                let checkInField = reservation.fechaEntrada || reservation.checkIn;
                let checkOutField = reservation.fechaSalida || reservation.checkOut;
                let checkIn, checkOut;
                
                if (typeof checkInField === 'object' && checkInField.seconds) {
                  checkIn = new Date(checkInField.seconds * 1000);
                } else if (checkInField instanceof Date) {
                  checkIn = checkInField;
                } else if (typeof checkInField === 'string') {
                  checkIn = new Date(checkInField);
                }
                
                if (typeof checkOutField === 'object' && checkOutField.seconds) {
                  checkOut = new Date(checkOutField.seconds * 1000);
                } else if (checkOutField instanceof Date) {
                  checkOut = checkOutField;
                } else if (typeof checkOutField === 'string') {
                  checkOut = new Date(checkOutField);
                }
                
                // Normalizar horas para comparación
                checkIn.setHours(0, 0, 0, 0);
                checkOut.setHours(0, 0, 0, 0);
                
                // Verificar si la reserva cruza al mes siguiente
                const nextMonthStart = new Date(currentYear, currentMonth + 1, 1);
                nextMonthStart.setHours(0, 0, 0, 0);
                
                const prevMonthEnd = new Date(currentYear, currentMonth, 0);
                prevMonthEnd.setHours(23, 59, 59, 999);
                
                // Comprobar si la reserva continúa al mes siguiente o viene del mes anterior
                const continuesNextMonth = checkOut >= nextMonthStart;
                const continuesFromPrevMonth = checkIn <= prevMonthEnd;
                
                // Log para depuración
                if (isLastDayOfMonth || continuesNextMonth) {
                  console.log(`Reserva en día ${day}/${currentMonth+1}/${currentYear}:`, {
                    id: reservation.id,
                    name: reservation.nombre || reservation.name,
                    checkIn: checkIn.toISOString().split('T')[0],
                    checkOut: checkOut.toISOString().split('T')[0],
                    isLastDay: isLastDayOfMonth,
                    continuesNextMonth: continuesNextMonth,
                    roomId: room.id
                  });
                }
                
                // Si es el primer día, marcar esta reserva como ya renderizada
                if (isFirstDay) {
                  // Usar el set local en lugar de setState
                  renderedReservations.add(reservationKey);
                  
                  // Calcular la duración de la reserva (en días)
                  const span = getReservationSpan(reservation);
                  
                  // Calcular cuántos días de la reserva están en el mes actual
                  const daysInCurrentMonth = Math.min(span, daysInMonth - day + 1);
                  
                  // Comprobar si es una reserva de bloqueo
                  const isBlocked = reservation.status === 'blocked' || 
                                 reservation.nombre === 'Bloqueado' || 
                                 reservation.name === 'Bloqueado';
                  
                  const isToday = isCurrentMonth && day === currentDay;
                  
                  // Para caso especial: reserva de un solo día que continúa al siguiente mes
                  // Por ejemplo, si empieza el 30 de abril y termina el 1 de mayo
                  const isSingleDayCrossover = isLastDayOfMonth && continuesNextMonth && daysInCurrentMonth === 1;
                  
                  // Si es una reserva de un solo día en el mes actual, usar un enfoque simplificado
                  if (daysInCurrentMonth === 1) {
                    return (
                      <div
                        key={`cell-${room.id}-${day}`}
                        className={`calendar-cell ${isBlocked ? 'blocked' : 'reserved'} ${isToday ? 'today' : ''} ${continuesNextMonth ? 'single-day-crossover' : ''}`}
                        onClick={() => handleCellClick(day, room.id)}
                      >
                        {!isBlocked && (
                          <div 
                            className="reservation-info"
                            title={`${reservation.nombre || reservation.name} - Entrada: ${formatTimestamp(reservation.fechaEntrada || reservation.checkIn)} - Salida: ${formatTimestamp(reservation.fechaSalida || reservation.checkOut)}`}
                          >
                            <span style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold'
                            }}>
                              {reservation.nombre || reservation.name}
                            </span>
                            {(reservation.personasEnHabitacion || reservation.guests) && 
                              <small className="guests-info">{reservation.personasEnHabitacion || reservation.guests} pers.</small>
                            }
                            {continuesNextMonth && (
                              <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                                {' →'}
                              </small>
                            )}
                          </div>
                        )}
                        {isBlocked && (
                          <div className="blocked-info">
                            <span>Bloqueado</span>
                            {continuesNextMonth && (
                              <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                                {' →'}
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Para reservas de múltiples días, usar grid-column: span 
                  // con indicador visual si continúa al mes siguiente
                  return (
                    <div
                      key={`cell-${room.id}-${day}`}
                      className={`calendar-cell ${isBlocked ? 'blocked' : 'reserved'} ${isToday ? 'today' : ''} ${continuesNextMonth ? 'continues-next-month' : ''}`}
                      onClick={() => handleCellClick(day, room.id)}
                      style={{ 
                        gridColumn: `span ${daysInCurrentMonth}`,
                        width: 'auto',
                        maxWidth: 'none',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        zIndex: 5
                      }}
                    >
                      {!isBlocked && (
                        <div 
                          className="reservation-info"
                          title={`${reservation.nombre || reservation.name} - Entrada: ${formatTimestamp(reservation.fechaEntrada || reservation.checkIn)} - Salida: ${formatTimestamp(reservation.fechaSalida || reservation.checkOut)}`}
                        >
                          <span style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold'
                          }}>
                            {reservation.nombre || reservation.name}
                          </span>
                          {(reservation.personasEnHabitacion || reservation.guests) && 
                            <small className="guests-info">{reservation.personasEnHabitacion || reservation.guests} pers.</small>
                          }
                          {continuesNextMonth && (
                            <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                              {' →'}
                            </small>
                          )}
                        </div>
                      )}
                      {isBlocked && (
                        <div className="blocked-info">
                          <span>Bloqueado</span>
                          {continuesNextMonth && (
                            <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                              {' →'}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Si no es el primer día pero no hemos renderizado esta reserva aún,
                  // es porque estamos empezando a ver una reserva que comenzó en el mes anterior
                  
                  // Calcular días restantes
                  checkOut.setHours(0, 0, 0, 0);
                  
                  const currentDate = new Date(currentYear, currentMonth, day);
                  currentDate.setHours(0, 0, 0, 0);
                  
                  const diffTime = Math.abs(checkOut - currentDate);
                  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  
                  // Comprobar si es una reserva de bloqueo
                  const isBlocked = reservation.status === 'blocked' || 
                                 reservation.nombre === 'Bloqueado' || 
                                 reservation.name === 'Bloqueado';
                  
                  const isToday = isCurrentMonth && day === currentDay;
                  
                  // Marcar como renderizada usando el set local
                  renderedReservations.add(reservationKey);
                  
                  // Si es una reserva que comienza el día 1 (viene del mes anterior)
                  if (isFirstDayOfMonth && continuesFromPrevMonth) {
                    return (
                      <div
                        key={`cell-${room.id}-${day}`}
                        className={`calendar-cell ${isBlocked ? 'blocked' : 'reserved'} ${isToday ? 'today' : ''} ${continuesFromPrevMonth ? 'continues-from-prev-month' : ''} first-day-continuation`}
                        onClick={() => handleCellClick(day, room.id)}
                        style={{ 
                          gridColumn: `span ${Math.min(daysLeft, daysInMonth)}`,
                          width: 'auto',
                          maxWidth: 'none',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          position: 'relative',
                          zIndex: 5
                        }}
                      >
                        {!isBlocked && (
                          <div 
                            className="reservation-info"
                            title={`${reservation.nombre || reservation.name} - Entrada: ${formatTimestamp(reservation.fechaEntrada || reservation.checkIn)} - Salida: ${formatTimestamp(reservation.fechaSalida || reservation.checkOut)}`}
                          >
                            <span style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold'
                            }}>
                              {reservation.nombre || reservation.name} 
                            </span>
                            {(reservation.personasEnHabitacion || reservation.guests) && 
                              <small className="guests-info">{reservation.personasEnHabitacion || reservation.guests} pers.</small>
                            }
                            {continuesNextMonth && (
                              <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                                {' →'}
                              </small>
                            )}
                          </div>
                        )}
                        {isBlocked && (
                          <div className="blocked-info">
                            <span>Bloqueado</span>
                            {continuesNextMonth && (
                              <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                                {' →'}
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Para reservas con múltiples días restantes, usar grid-column: span
                  return (
                    <div
                      key={`cell-${room.id}-${day}`}
                      className={`calendar-cell ${isBlocked ? 'blocked' : 'reserved'} ${isToday ? 'today' : ''}`}
                      onClick={() => handleCellClick(day, room.id)}
                      style={{ 
                        gridColumn: `span ${Math.min(daysLeft, daysInMonth - day + 1)}`,
                        width: 'auto',
                        maxWidth: 'none',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        zIndex: 5
                      }}
                    >
                      {!isBlocked && (
                        <div 
                          className="reservation-info"
                          title={`${reservation.nombre || reservation.name} - Entrada: ${formatTimestamp(reservation.fechaEntrada || reservation.checkIn)} - Salida: ${formatTimestamp(reservation.fechaSalida || reservation.checkOut)}`}
                        >
                          <span style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold'
                          }}>
                            {reservation.nombre || reservation.name}
                          </span>
                          {(reservation.personasEnHabitacion || reservation.guests) && 
                            <small className="guests-info">{reservation.personasEnHabitacion || reservation.guests} pers.</small>
                          }
                          {continuesNextMonth && (
                            <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                              {' →'}
                            </small>
                          )}
                        </div>
                      )}
                      {isBlocked && (
                        <div className="blocked-info">
                          <span>Bloqueado</span>
                          {continuesNextMonth && (
                            <small style={{ display: 'block', textAlign: 'center', fontSize: '0.75rem' }}>
                              {' →'}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          ))}
      </div>
    );
  };

  // Función para generar y descargar PDF con las reservas del mes
  const generarPDF = () => {
    try {
      // Crear instancia de jsPDF
      const pdf = new jsPDF();
      
      // Título del documento
      pdf.setFontSize(18);
      pdf.text(`Reservas - ${months[currentMonth]} ${currentYear}`, 14, 20);
      
      // Información adicional
      pdf.setFontSize(12);
      pdf.text(`Generado el: ${new Date().toLocaleDateString()}`, 14, 30);
      pdf.text(`Total de reservas: ${reservations.length}`, 14, 36);
      
      // Preparar datos para la tabla
      const tableRows = [];
      
      reservations.forEach(res => {
        // Obtener los campos independientemente del nombre
        const nombre = res.nombre || res.name || 'Sin nombre';
        const roomId = res.habitacionId || res.roomId || '';
        const guests = res.personasEnHabitacion || res.guests || '';
        
        // Convertir fechas a formato legible
        let checkIn, checkOut;
        
        // Manejar fechas de entrada
        if (res.fechaEntrada && typeof res.fechaEntrada === 'object' && res.fechaEntrada.seconds) {
          checkIn = new Date(res.fechaEntrada.seconds * 1000).toLocaleDateString();
        } else if (res.checkIn && typeof res.checkIn === 'object' && res.checkIn.seconds) {
          checkIn = new Date(res.checkIn.seconds * 1000).toLocaleDateString();
        } else if (res.fechaEntrada instanceof Date) {
          checkIn = res.fechaEntrada.toLocaleDateString();
        } else if (res.checkIn instanceof Date) {
          checkIn = res.checkIn.toLocaleDateString();
        } else {
          checkIn = res.fechaEntrada || res.checkIn || '-';
          if (typeof checkIn === 'string' && checkIn.includes('T')) {
            checkIn = new Date(checkIn).toLocaleDateString();
          }
        }
        
        // Manejar fechas de salida
        if (res.fechaSalida && typeof res.fechaSalida === 'object' && res.fechaSalida.seconds) {
          checkOut = new Date(res.fechaSalida.seconds * 1000).toLocaleDateString();
        } else if (res.checkOut && typeof res.checkOut === 'object' && res.checkOut.seconds) {
          checkOut = new Date(res.checkOut.seconds * 1000).toLocaleDateString();
        } else if (res.fechaSalida instanceof Date) {
          checkOut = res.fechaSalida.toLocaleDateString();
        } else if (res.checkOut instanceof Date) {
          checkOut = res.checkOut.toLocaleDateString();
        } else {
          checkOut = res.fechaSalida || res.checkOut || '-';
          if (typeof checkOut === 'string' && checkOut.includes('T')) {
            checkOut = new Date(checkOut).toLocaleDateString();
          }
        }
        
        // Obtener nombre de habitación a partir del ID
        const habitacion = rooms.find(h => h.id.toString() === roomId.toString());
        const nombreHabitacion = habitacion ? habitacion.name : `Habitación ${roomId}`;
        
        // Agregar fila a la tabla
        tableRows.push([
          nombre,
          nombreHabitacion,
          checkIn,
          checkOut,
          guests,
          res.precioTotal || res.totalPrice || '-'
        ]);
      });
      
      // Definir columnas
      const tableColumns = [
        { header: 'Cliente', dataKey: 'cliente' },
        { header: 'Habitación', dataKey: 'habitacion' },
        { header: 'Check-in', dataKey: 'checkin' },
        { header: 'Check-out', dataKey: 'checkout' },
        { header: 'Huéspedes', dataKey: 'huespedes' },
        { header: 'Precio Total', dataKey: 'precio' }
      ];
      
      // Generar tabla en el PDF con autoTable
      autoTable(pdf, {
        head: [tableColumns.map(col => col.header)],
        body: tableRows,
        startY: 45,
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });
      
      // Guardar el PDF
      pdf.save(`Reservas_${months[currentMonth]}_${currentYear}.pdf`);
      
      setSuccess("PDF generado correctamente");
    } catch (err) {
      console.error("Error al generar PDF:", err);
      setError(`Error al generar PDF: ${err.message}`);
    }
  };

  // Función para probar el envío de correos
  const testEmailSending = async () => {
    try {
      setLoading(true);
      
      console.log("Probando envío de correo electrónico...");
      
      // Crear datos de prueba para el correo
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const todayStr = today.toISOString().split('T')[0];
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      
      const roomId = 1;
      const habitacionPrueba = rooms.find(h => h.id.toString() === roomId.toString());
      
      // Preguntar al usuario si quiere probar con notificación al administrador
      const probarConNotificacionAdmin = window.confirm(
        "¿Quieres probar el envío de correo incluyendo la notificación al administrador?\n\n" +
        "- Presiona OK para simular una reserva creada por un cliente (con notificación al administrador)\n" +
        "- Presiona Cancelar para simular una reserva creada desde el panel de administración (sin notificación al administrador)"
      );
      
      // Objeto de reserva adaptado para enviar correo
      const reservaParaCorreo = {
        id: "test-email-" + Date.now(),
        nombre: "Prueba de Email",
        email: "test@example.com", // Puedes cambiar esto a tu correo para probar
        telefono: "123456789",
        fechaEntrada: todayStr,
        fechaSalida: tomorrowStr,
        habitaciones: newReservation.rooms.map(roomId => ({
          id: roomId,
          nombre: rooms.find(h => h.id.toString() === roomId.toString()).nombre || `Habitación ${roomId}`,
          name: rooms.find(h => h.id.toString() === roomId.toString()).name || `Habitación ${roomId}`,
          tipo: rooms.find(h => h.id.toString() === roomId.toString()).tipo,
          capacidad: rooms.find(h => h.id.toString() === roomId.toString()).capacidad,
          precio: 33000,
          preciosPorOcupacion: rooms.find(h => h.id.toString() === roomId.toString()).preciosPorOcupacion || {}
        })),
        distribucionPersonas: { [newReservation.rooms[0]]: newReservation.rooms.length },
        duracionEstancia: 1,
        precioTotal: 33000,
        codigoReserva: "TEST" + Math.floor(1000 + Math.random() * 9000).toString()
      };
      
      console.log("Enviando correo de prueba con datos:", reservaParaCorreo);
      console.log("Incluir notificación al administrador:", probarConNotificacionAdmin);
      
      const emailResult = await enviarCorreoConfirmacion(reservaParaCorreo, probarConNotificacionAdmin);
      
      if (emailResult) {
        setSuccess(`Correo de prueba enviado exitosamente (o simulado en entorno local). 
          ${probarConNotificacionAdmin ? 'Se incluyó notificación al administrador.' : 'NO se incluyó notificación al administrador.'}
          Revisa la consola para más detalles.`);
      } else {
        setError("Falló el envío del correo de prueba. Revisa la consola para más detalles.");
      }
    } catch (err) {
      console.error("Error en prueba de correo:", err);
      setError(`Error al probar envío de correo: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Crear función para calcular precio total
  const calculateTotalPrice = async (roomsArray, occupancyObj, days) => {
    try {
      let totalPrice = 0;
      const newRoomPrices = {};
      
      // Para cada habitación seleccionada
      for (const roomId of roomsArray) {
        // Obtener ocupación de esta habitación
        const occupancy = occupancyObj[roomId] || 1;
        
        // Calcular precio por habitación y ocupación
        const roomPrice = await calculateRoomPrice(roomId, occupancy);
        
        // Guardar el precio por habitación
        newRoomPrices[roomId] = roomPrice;
        
        // Sumar al precio total
        totalPrice += roomPrice * days;
      }
      
      // Actualizar los precios por habitación
      setRoomPrices(newRoomPrices);
      
      // Calcular precio por noche (promedio si hay múltiples habitaciones)
      const pricePerNight = roomsArray.length > 0 ? Math.round(totalPrice / days / roomsArray.length) : 0;
      
      // Actualizar el precio total y precio por noche en la reserva
      setNewReservation(prev => ({
        ...prev,
        totalPrice: totalPrice,
        pricePerNight: pricePerNight
      }));
      
      return totalPrice;
    } catch (error) {
      console.error("Error al calcular precio total:", error);
      return 0;
    }
  };

  return (
    <div className="admin-calendario">
      <h1>Administrar Reservaciones</h1>
      
      {/* Mensajes de éxito/error */}
      {error && (
        <div className="message error">
          {error}
          <button onClick={handleCloseMessage}>&times;</button>
        </div>
      )}
      
      {success && (
        <div className="message success">
          {success}
          <button onClick={handleCloseMessage}>&times;</button>
        </div>
      )}
      
      {/* Encabezado del calendario */}
      <div className="calendar-header">
        <button className="nav-button" onClick={handlePrevMonth}>&laquo; Mes anterior</button>
        <h2>{months[currentMonth]} {currentYear}</h2>
        <div className="nav-buttons-group">
          <button className="nav-button today-button" onClick={scrollToCurrentDay}>
            Ir a hoy
          </button>
          <button className="nav-button" onClick={handleNextMonth}>Mes siguiente &raquo;</button>
        </div>
      </div>
      
      {/* Botones de acción (rediseñados) */}
      <div className="action-buttons-container">
        <div className="desktop-buttons">
          <button 
            className="nav-button action-button"
            onClick={() => fetchReservationsForMonth(currentMonth, currentYear)}
            style={{ backgroundColor: '#28a745' }}
          >
            🔄 Recargar datos
          </button>
          
          <button 
            className="nav-button action-button"
            onClick={startMultiBlockMode}
            style={{ backgroundColor: '#dc3545' }}
          >
            🚫 Bloquear múltiples habitaciones
          </button>
          
          <button 
            className="nav-button action-button"
            onClick={generarPDF}
            style={{ backgroundColor: '#007bff' }}
          >
            📄 Descargar PDF
          </button>
        </div>
        
        <button 
          className="nav-button action-button tools-toggle"
          onClick={() => setShowAdvancedTools(!showAdvancedTools)}
          style={{ 
            backgroundColor: '#6c757d',
            position: 'relative'
          }}
        >
          🛠️ {showAdvancedTools ? 'Ocultar herramientas' : 'Más herramientas'}
        </button>
        
        {showAdvancedTools && (
          <div className="advanced-tools-menu">
            <div className="mobile-buttons">
              <button 
                className="nav-button action-button"
                onClick={() => fetchReservationsForMonth(currentMonth, currentYear)}
                style={{ backgroundColor: '#28a745' }}
              >
                🔄 Recargar datos
              </button>
              
              <button 
                className="nav-button action-button"
                onClick={startMultiBlockMode}
                style={{ backgroundColor: '#dc3545' }}
              >
                🚫 Bloquear múltiples habitaciones
              </button>
              
              <button 
                className="nav-button action-button"
                onClick={generarPDF}
                style={{ backgroundColor: '#007bff' }}
              >
                📄 Descargar PDF
              </button>
            </div>
            
            <button 
              className="nav-button action-button"
              onClick={checkFirebaseConnection}
              style={{ backgroundColor: '#6c757d' }}
            >
              🔌 Verificar conexión
            </button>
          </div>
        )}
      </div>
      
      <div className="reservations-info-container">
        <p><strong>Reservas cargadas:</strong> {reservations.length}</p>
        {reservations.length === 0 && (
          <p>No hay reservas para mostrar en este periodo.</p>
        )}
      </div>
      
      {/* Contenedor del calendario */}
      {loading ? (
        <p>Cargando calendario...</p>
      ) : (
        <div className="calendar-container">
          {renderCalendar()}
        </div>
      )}
      
      {/* Modal para añadir/editar reservas */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>
              {modalMode === 'add' ? 'Crear Reserva' : 'Editar Reserva'}
              {editingReservation?.isMultipleReservation && (
                <span className="multiple-reservation-badge">
                  Reserva múltiple - {editingReservation.relatedReservationsIds?.length || 0} habitaciones
                </span>
              )}
            </h2>
            
            {modalMode === 'add' && (
              <div className="info-block" style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '10px', 
                borderRadius: '5px', 
                marginBottom: '15px',
                border: '1px solid #ddd'
              }}>
                <p style={{ margin: '0', fontSize: '14px' }}>
                  <strong>Tip:</strong> Para bloquear fechas deje los campos de nombre, email y teléfono vacíos. 
                  El sistema creará automáticamente una reserva de tipo "Bloqueado".
                </p>
              </div>
            )}
            
            <form onSubmit={handleFormSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nombre</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newReservation.name}
                  onChange={handleFormChange}
                  placeholder="Dejar vacío para bloquear día"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Correo Electrónico</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={newReservation.email}
                  onChange={handleFormChange}
                  placeholder="Opcional para día bloqueado"
                />
              </div>
              
              {/* Checkbox para enviar correo (solo en modo agregar) */}
              {modalMode === 'add' && (
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    name="sendEmail"
                    checked={newReservation.sendEmail}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="sendEmail">Enviar correo de confirmación al crear la reserva</label>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={newReservation.phone}
                  onChange={handleFormChange}
                  placeholder="Opcional para día bloqueado"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="checkIn">Fecha de entrada</label>
                <input
                  type="date"
                  className="form-control"
                  id="checkIn"
                  name="checkIn"
                  value={
                    typeof newReservation.checkIn === 'string' 
                      ? newReservation.checkIn 
                      : newReservation.checkIn instanceof Date 
                        ? newReservation.checkIn.toISOString().split('T')[0] 
                        : newReservation.checkIn && typeof newReservation.checkIn === 'object' && newReservation.checkIn.seconds
                          ? new Date(newReservation.checkIn.seconds * 1000).toISOString().split('T')[0]
                          : ''
                  }
                  onChange={handleFormChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="checkOut">Fecha de salida</label>
                <input
                  type="date"
                  className="form-control"
                  id="checkOut"
                  name="checkOut"
                  value={
                    typeof newReservation.checkOut === 'string' 
                      ? newReservation.checkOut 
                      : newReservation.checkOut instanceof Date 
                        ? newReservation.checkOut.toISOString().split('T')[0] 
                        : newReservation.checkOut && typeof newReservation.checkOut === 'object' && newReservation.checkOut.seconds
                          ? new Date(newReservation.checkOut.seconds * 1000).toISOString().split('T')[0]
                          : ''
                  }
                  onChange={handleFormChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="duration">Duración de estancia (días)</label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  min="1"
                  value={newReservation.duration}
                  onChange={handleFormChange}
                  readOnly
                />
              </div>
              
              <div className="form-group">
                <label>Selección de Habitaciones</label>
                <div className="selected-rooms-container">
                  {selectedRooms.map(roomId => {
                    const room = rooms.find(r => r.id.toString() === roomId.toString());
                    return (
                      <div key={roomId} className="selected-room-item">
                        <div className="room-info">
                          <span>Habitación {room.id}</span>
                <select
                            value={roomOccupancy[roomId] || 1}
                            onChange={(e) => handleRoomSelection(roomId, parseInt(e.target.value))}
                          >
                            {Array.from({ length: room.capacidad }, (_, i) => i + 1).map(num => (
                              <option key={num} value={num}>
                                {num} {num === 1 ? 'persona' : 'personas'}
                      </option>
                            ))}
                </select>
                        </div>
                        <button
                          type="button"
                          className="remove-room-btn"
                          onClick={() => handleRemoveRoom(roomId)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
              </div>
              
                <select
                  className="add-room-select"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleRoomSelection(e.target.value, 1);
                      e.target.value = ''; // Reset select
                    }
                  }}
                >
                  <option value="">+ Agregar habitación</option>
                  {rooms
                    .filter(room => !selectedRooms.includes(room.id.toString()))
                    .map(room => (
                      <option key={room.id} value={room.id}>
                        Habitación {room.id} (Max: {room.capacidad} personas)
                        </option>
                    ))
                    }
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="pricePerNight">Precio por noche</label>
                <input
                  type="number"
                  id="pricePerNight"
                  name="pricePerNight"
                  min="0"
                  value={newReservation.pricePerNight || 0}
                  onChange={handleFormChange}
                  required
                />
                <small className="form-text text-muted">
                  Precio por noche y por habitación (promedio). El precio total será {newReservation.pricePerNight || 0} × {newReservation.duration || 1} noches × {selectedRooms.length || 1} habitaciones = ${(newReservation.pricePerNight || 0) * (newReservation.duration || 1) * (selectedRooms.length || 1)}
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="tipoPago">Tipo de pago</label>
                <select
                  id="tipoPago"
                  name="tipoPago"
                  value={newReservation.tipoPago}
                  onChange={handleFormChange}
                  required
                >
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Tarjeta">Tarjeta</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="reservationCode">Código de reserva</label>
                <input
                  type="text"
                  id="reservationCode"
                  name="reservationCode"
                  value={newReservation.reservationCode}
                  onChange={handleFormChange}
                  readOnly
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="syncWithCalendar">Sincronizar con calendario</label>
                <input
                  type="checkbox"
                  id="syncWithCalendar"
                  name="syncWithCalendar"
                  checked={newReservation.syncWithCalendar}
                  onChange={handleFormChange}
                />
              </div>
              
              {newReservation.syncWithCalendar && (
                <div className="form-group">
                  <label htmlFor="calendarEventId">ID de evento en calendario</label>
                  <input
                    type="text"
                    id="calendarEventId"
                    name="calendarEventId"
                    value={newReservation.calendarEventId}
                    onChange={handleFormChange}
                  />
                </div>
              )}
              
              {selectedRooms.length > 0 && (
                <div className="price-summary-container">
                  <h4>Detalle de precios</h4>
                  <div className="price-summary-list">
                    {selectedRooms.map(roomId => {
                      const room = rooms.find(r => r.id.toString() === roomId);
                      const occupancy = roomOccupancy[roomId] || 1;
                      const nightlyRate = newReservation.pricePerNight || roomPrices[roomId] || 0;
                      
                      return (
                        <div key={roomId} className="price-summary-item">
                          <div className="price-summary-details">
                            <span className="room-name">Habitación {roomId}</span>
                            <span className="room-occupancy">{occupancy} {occupancy === 1 ? 'persona' : 'personas'}</span>
                          </div>
                          <div className="price-summary-calculation">
                            <span className="price-value">${nightlyRate}</span>
                            <span className="price-multiply">×</span>
                            <span className="price-nights">{newReservation.duration} {newReservation.duration === 1 ? 'noche' : 'noches'}</span>
                            <span className="price-equals">=</span>
                            <span className="price-total">${nightlyRate * newReservation.duration}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="price-grand-total">
                    <span>Total:</span>
                    <span className="grand-total-value">${newReservation.totalPrice}</span>
                  </div>
                </div>
              )}
              
              <div className="form-actions">
                {modalMode === 'edit' && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={handleDeleteReservation}
                  >
                    Eliminar
                  </button>
                )}
                
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  className={modalMode === 'add' ? 'save-button' : 'update-button'}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {modalMode === 'add' ? 'Guardando...' : 'Actualizando...'}
                    </>
                  ) : (
                    modalMode === 'add' ? 'Guardar' : 'Actualizar'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Panel de bloqueo múltiple */}
      {multiBlockMode && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Bloquear Múltiples Habitaciones</h2>
            <div className="form-group">
              <label>Fecha de inicio:</label>
              <input
                type="date"
                value={blockStartDate}
                onChange={(e) => setBlockStartDate(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Fecha de fin:</label>
              <input
                type="date"
                value={blockEndDate}
                onChange={(e) => setBlockEndDate(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Seleccione las habitaciones a bloquear:</label>
              <div className="rooms-grid">
                {rooms.map(room => (
                  <div 
                    key={room.id} 
                    className={`room-select-box ${selectedRooms.includes(room.id) ? 'selected' : ''}`}
                    onClick={() => handleRoomSelection(room.id)}
                  >
                    <div className="room-number">Habitación {room.id}</div>
                    <div className="room-type">{room.tipo}</div>
                    <div className="room-capacity">{room.capacidad} personas</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button 
                className="cancel-button"
                onClick={() => {
                  setMultiBlockMode(false);
                  setSelectedRooms([]);
                }}
              >
                Cancelar
              </button>
              <button
                className="save-button"
                onClick={confirmMultiBlock}
                disabled={isSubmitting || !blockStartDate || !blockEndDate || selectedRooms.length === 0}
              >
                {isSubmitting ? 'Bloqueando...' : 'Confirmar Bloqueo'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Componente de menú flotante */}
      <FloatingMenu />
    </div>
  );
};

export default AdminCalendario; 